/**
 * Chat queries — conversations, messages, feedback, saved messages, and folders.
 *
 * Centralizes all chat-related Supabase calls. Any change to chat tables,
 * columns, or edge function payloads should only need to be updated here.
 *
 * Used by: ChatContext, ChatMessages, SavedMessagesPanel, ConversationFolders
 */
import { supabase } from '@/lib/supabase'
import { captureException } from '@/lib/sentry'
import { invokeEdgeJson, invokeEdgeStream } from './_edge'
import { CHAT, SYNTHESIZE, TITLE_GEN, TRUNCATE_CONVERSATION } from './_endpoints'
import {
  CHAT_SEARCH_RESULT_LIMIT,
  SHARE_TOKEN_ALPHABET,
  SHARE_TOKEN_LENGTH,
  TITLE_GEN_MAX_CHARS,
} from './_constants'
import { listForUser } from './_helpers'
import {
  CHAT_ATTACHMENT_BUCKET,
  CHAT_ATTACHMENT_SIGNED_URL_SECONDS,
  CHAT_MAX_ATTACHMENT_BYTES,
  CHAT_ACCEPTED_IMAGE_TYPES,
} from '@/lib/constants'

// ── Conversations ───────────────────────────────────────────────

/** Load all conversations for a user, ordered by most recent.
 *
 * Filters out ghost rows with 0 messages. Ghosts happen when a chat request
 * is aborted mid-stream (network blip, user navigation, rate-limit). The edge
 * function writes the conversation row atomically with messages now, but
 * pre-fix ghosts still exist in the DB and we don't want them in the UI. */
export async function fetchConversations(userId) {
  const { data, error } = await supabase
    .from('conversations')
    .select('*, messages(count)')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false })
  if (error) throw error
  return (data || []).filter(c => (c.messages?.[0]?.count ?? 0) > 0)
}

/** Load all messages for a conversation. */
export async function fetchMessages(conversationId) {
  const { data, error } = await supabase
    .from('messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at')
  if (error) throw error
  return data || []
}

/** Delete a conversation by ID. */
export async function deleteConversation(conversationId) {
  const { error } = await supabase.from('conversations').delete().eq('id', conversationId)
  if (error) throw error
}

/**
 * Full-text search the user's conversations by title + message body. Powered
 * by the `search_user_conversations` Postgres RPC: title-hits get a 2x rank
 * bonus over body-hits, ts_headline emits `<b>...</b>`-wrapped snippets,
 * deleted_at IS NULL filtering happens server-side, and results are capped
 * at CHAT_SEARCH_RESULT_LIMIT. Returns [] for empty/whitespace queries.
 */
export async function searchConversations(userId, query) {
  if (!userId) return []
  const trimmed = (query || '').trim()
  if (!trimmed) return []
  try {
    const { data, error } = await supabase.rpc('search_user_conversations', {
      p_user_id: userId,
      p_query: trimmed,
    })
    if (error) throw error
    return (data || []).slice(0, CHAT_SEARCH_RESULT_LIMIT)
  } catch (err) {
    captureException(err, { tags: { feature: 'chat', op: 'search' } })
    throw err
  }
}

/**
 * Fetch the user's conversations filtered by per-message engagement —
 * "saved" returns conversations that contain at least one bookmarked
 * message; "liked" returns conversations with at least one helpful vote;
 * "flagged" returns conversations the user flagged for review. Two-step
 * fetch (conversation IDs → conversations) keeps the join simple in
 * PostgREST and avoids RLS-policy joins on the engagement tables.
 */
export async function fetchConversationsByEngagement(userId, kind) {
  if (!userId || !['saved', 'liked', 'flagged'].includes(kind)) return []
  // Step 1: pull the message IDs the user engaged with via that channel.
  let messageIds = []
  if (kind === 'saved') {
    const { data, error } = await supabase
      .from('saved_messages')
      .select('message_id')
      .eq('user_id', userId)
    if (error) throw error
    messageIds = (data || []).map(r => r.message_id).filter(Boolean)
  } else {
    const vote = kind === 'liked' ? 'like' : 'dislike'
    const { data, error } = await supabase
      .from('message_feedback')
      .select('message_id')
      .eq('user_id', userId)
      .eq('vote', vote)
    if (error) throw error
    messageIds = (data || []).map(r => r.message_id).filter(Boolean)
  }
  if (messageIds.length === 0) return []
  // Step 2: resolve those message IDs to their conversations.
  const { data: msgRows, error: msgErr } = await supabase
    .from('messages')
    .select('conversation_id')
    .in('id', messageIds)
  if (msgErr) throw msgErr
  const convIds = Array.from(new Set((msgRows || []).map(r => r.conversation_id).filter(Boolean)))
  if (convIds.length === 0) return []
  const { data, error } = await supabase
    .from('conversations')
    .select('*, messages(count)')
    .in('id', convIds)
    .eq('user_id', userId)
    .order('updated_at', { ascending: false })
  if (error) throw error
  return (data || []).filter(c => (c.messages?.[0]?.count ?? 0) > 0)
}

// ── Title Generation ────────────────────────────────────────────

/** Generate an AI title for a conversation via edge function. */
export async function generateTitle(conversationId, userMessage, assistantMessage) {
  return invokeEdgeJson(
    TITLE_GEN,
    {
      conversationId,
      userMessage,
      assistantMessage: assistantMessage?.substring(0, TITLE_GEN_MAX_CHARS),
    },
    { errorMessage: 'Title generation failed' }
  )
}

// ── Send Message (Streaming) ────────────────────────────────────

/**
 * Send a chat message to the edge function.
 * Returns a ReadableStream for SSE parsing.
 * @param {Object} payload - { message, conversationId, tab, ... }
 * @param {Object} [options] - { signal } for abort control
 * @returns {Promise<Response>} Raw fetch response for stream reading
 */
export async function sendChatMessage(payload, { signal } = {}) {
  return invokeEdgeStream(CHAT, payload, { signal, errorMessage: 'Chat request failed' })
}

// ── Chat V2 (chat-redesign) — multi-voice helpers ──────────────
// These exist to support the V4 layout's fan-out flow: pre-create the
// conversation and the user message ONCE, then fire N parallel
// sendChatMessage calls (one per active voice) all sharing the same
// conversationId. Existing /chat is unaffected.

/** Create a conversation row up-front so multi-voice fan-out can share its id.
 *  `tab` should be the primary voice's tab; the edge function will branch on
 *  it for the primary voice's call (other voices send their own tab).
 *  `metadata` is the chat-v2 shell state — mode/anchor/voices/lenses_active/
 *  complexity/bible_version — so reload restores the same shape. */
export async function createConversationV2({
  userId,
  tab = 'general',
  title = 'New conversation',
  metadata = {},
}) {
  const { data, error } = await supabase
    .from('conversations')
    .insert({ user_id: userId, tab, title, metadata })
    .select('id')
    .single()
  if (error) throw error
  return data.id
}

/** Update a conversation's chat-v2 metadata bag. Caller is expected to send
 *  the whole bag; we don't merge server-side. */
export async function updateConversationMetadata(conversationId, metadata) {
  const { error } = await supabase
    .from('conversations')
    .update({ metadata })
    .eq('id', conversationId)
  if (error) throw error
}

/** Load a single conversation row + ALL its messages. The frontend groups
 *  messages into turns (each user message starts a turn; following assistant
 *  messages with metadata.voice attach to it; metadata.is_synthesis attaches
 *  as the turn's synthesis). */
export async function fetchConversationFull(conversationId) {
  const [convRes, msgsRes] = await Promise.all([
    supabase
      .from('conversations')
      .select('id, user_id, tab, title, metadata, summary, created_at, updated_at')
      .eq('id', conversationId)
      .single(),
    supabase
      .from('messages')
      .select('id, role, content, attachments, metadata, created_at')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true }),
  ])
  if (convRes.error) throw convRes.error
  if (msgsRes.error) throw msgsRes.error
  return {
    conversation: convRes.data,
    messages: msgsRes.data || [],
  }
}

/** Insert the user's message row once before fanning out. Subsequent
 *  sendChatMessage calls must pass `skipUserInsert: true` so we don't
 *  duplicate it N times.
 *
 *  `metadata` carries chat-v2 turn shape — voice_order (the order voices
 *  were displayed in this turn) and primary_voice (which one's reply
 *  drives titles + autoroute). Persisted on the USER row so reload
 *  rebuilds the same column layout regardless of which assistant row
 *  landed first. Empty by default so callers that don't care fall
 *  through to the column's '{}'::jsonb default.
 *
 *  `attachments` is the ready-to-persist payload array (e.g. signed-storage
 *  refs returned from `useChatAttachments.getReadyForSend()`). Persisted
 *  on the user row so reload / share / export can restore image thumbnails
 *  on the user pill (T2-B fix). */
export async function insertUserMessage({
  conversationId,
  content,
  metadata = null,
  attachments = [],
}) {
  const row = {
    conversation_id: conversationId,
    role: 'user',
    content,
    attachments: Array.isArray(attachments) ? attachments : [],
  }
  if (metadata && typeof metadata === 'object') row.metadata = metadata
  const { data, error } = await supabase.from('messages').insert(row).select('id').single()
  if (error) throw error
  return data.id
}

/** Stream a synthesis of N voice answers via the synthesize edge function.
 *  Returns the raw Response so the caller can read the SSE body. */
export async function sendSynthesize(payload, { signal } = {}) {
  return invokeEdgeStream(SYNTHESIZE, payload, { signal, errorMessage: 'Synthesis failed' })
}

/** Hard-delete every message at-or-after `fromMessageId` in a conversation
 *  the caller owns. Edge function performs the delete with service-role
 *  (messages RLS doesn't grant DELETE to authenticated). Used by chat-v2
 *  edit-and-resend so the original turn is replaced, not orphaned (T3-B). */
export async function truncateConversationFrom({ conversationId, fromMessageId }) {
  if (!conversationId || !fromMessageId) {
    throw new Error('conversationId and fromMessageId are required')
  }
  return invokeEdgeJson(
    TRUNCATE_CONVERSATION,
    { conversationId, fromMessageId },
    { errorMessage: 'Could not replace the previous turn — please retry.' }
  )
}

// ── Chat Attachments (images) ───────────────────────────────────

/**
 * Upload a chat image attachment to Supabase Storage under the user's folder.
 * Returns the storage path so it can be forwarded to the chat edge function.
 * Caller should validate type/size via shouldAcceptAttachment() first.
 */
export async function uploadChatAttachment(userId, file) {
  if (!userId) throw new Error('Not signed in')
  if (!CHAT_ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    throw new Error('Only images are supported (PNG, JPG, WEBP, GIF, HEIC).')
  }
  if (file.size > CHAT_MAX_ATTACHMENT_BYTES) {
    throw new Error('Image is too large — keep it under 10 MB.')
  }
  const ext = (file.name.split('.').pop() || 'bin').toLowerCase().replace(/[^a-z0-9]/g, '')
  const key = `${userId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext || 'img'}`
  const { error } = await supabase.storage
    .from(CHAT_ATTACHMENT_BUCKET)
    .upload(key, file, { contentType: file.type, upsert: false })
  if (error) throw error
  return { path: key, contentType: file.type, size: file.size, name: file.name }
}

/** Create a short-lived signed URL so the browser can render a private attachment. */
export async function getAttachmentSignedUrl(path) {
  const { data, error } = await supabase.storage
    .from(CHAT_ATTACHMENT_BUCKET)
    .createSignedUrl(path, CHAT_ATTACHMENT_SIGNED_URL_SECONDS)
  if (error) throw error
  return data.signedUrl
}

/** Best-effort delete used when the user removes a pending attachment before sending. */
export async function removeChatAttachment(path) {
  if (!path) return
  await supabase.storage
    .from(CHAT_ATTACHMENT_BUCKET)
    .remove([path])
    .catch(() => {})
}

// ── Saved Messages ──────────────────────────────────────────────

/** Load all saved messages for a user. */
export async function fetchSavedMessages(userId) {
  return listForUser('saved_messages', {
    userId,
    orderBy: 'saved_at',
    ascending: false,
  })
}

/**
 * Load saved messages enriched with each one's `conversation_id` (for
 * jump-to-conversation links on the /saved hub). Two-step fetch — saved
 * rows first, then a single batched lookup against `messages`.
 * Saved-message rows whose underlying message has been deleted are
 * silently dropped from the result.
 */
export async function fetchSavedMessagesWithConversation(userId) {
  if (!userId) return []
  const saved = await fetchSavedMessages(userId)
  if (!saved || saved.length === 0) return []
  const messageIds = saved.map(s => s.message_id).filter(Boolean)
  if (messageIds.length === 0) return saved.map(s => ({ ...s, conversation_id: null }))
  const { data: msgRows, error } = await supabase
    .from('messages')
    .select('id, conversation_id')
    .in('id', messageIds)
  if (error) throw error
  const convoByMid = new Map((msgRows || []).map(m => [m.id, m.conversation_id]))
  return saved
    .map(s => ({ ...s, conversation_id: convoByMid.get(s.message_id) ?? null }))
    .filter(s => s.conversation_id) // drop rows whose underlying message is gone
}

/** Save a message (bookmark). */
export async function saveMessage({ userId, messageId, content, tab }) {
  const { error } = await supabase.from('saved_messages').insert({
    user_id: userId,
    message_id: messageId,
    content,
    tab,
  })
  if (error) throw error
}

/** Delete a saved message by ID. */
export async function deleteSavedMessage(id) {
  const { error } = await supabase.from('saved_messages').delete().eq('id', id)
  if (error) throw error
}

/** Delete a saved message by content match. */
export async function deleteSavedMessageByContent(userId, content) {
  const { error } = await supabase
    .from('saved_messages')
    .delete()
    .eq('user_id', userId)
    .eq('content', content)
  if (error) throw error
}

/** Delete a saved message by message_id — preferred over the content
 *  match, which can wipe two saved rows when the user happens to have
 *  bookmarked two messages with identical wording (e.g. regenerate that
 *  returned the same answer). Caller is the one toggling the bookmark
 *  off in useMessageActions; safe to be a no-op when no row matches. */
export async function deleteSavedMessageByMessageId(userId, messageId) {
  if (!userId || !messageId) return
  const { error } = await supabase
    .from('saved_messages')
    .delete()
    .eq('user_id', userId)
    .eq('message_id', messageId)
  if (error) throw error
}

// ── Message Feedback ────────────────────────────────────────────

/** Load all feedback votes for a user's messages. */
export async function fetchMessageFeedback(userId) {
  return listForUser('message_feedback', {
    userId,
    select: 'message_id, vote, reason',
  })
}

/** Submit a like vote on a message. */
export async function likeMessage({ userId, messageId, tab }) {
  const { error } = await supabase
    .from('message_feedback')
    .upsert(
      { user_id: userId, message_id: messageId, tab, vote: 'like', reason: null },
      { onConflict: 'user_id,message_id' }
    )
  if (error) throw error
}

/** Submit a dislike vote with reason. */
export async function dislikeMessage({ userId, messageId, tab, reason }) {
  const { error } = await supabase
    .from('message_feedback')
    .upsert(
      { user_id: userId, message_id: messageId, tab, vote: 'dislike', reason },
      { onConflict: 'user_id,message_id' }
    )
  if (error) throw error
}

/** Remove feedback vote from a message. */
export async function removeFeedback(userId, messageId) {
  const { error } = await supabase
    .from('message_feedback')
    .delete()
    .eq('user_id', userId)
    .eq('message_id', messageId)
  if (error) throw error
}

// ── Conversation Folders ────────────────────────────────────────

/** Load all conversation folders for a user. */
export async function fetchFolders(userId) {
  return listForUser('conversation_folders', {
    userId,
    orderBy: 'sort_order',
  })
}

/** Create a new conversation folder. */
export async function createFolder({ userId, name, emoji }) {
  const { data, error } = await supabase
    .from('conversation_folders')
    .insert({ user_id: userId, name, emoji })
    .select()
    .single()
  if (error) throw error
  return data
}

/** Delete a conversation folder. */
export async function deleteFolder(folderId) {
  const { error } = await supabase.from('conversation_folders').delete().eq('id', folderId)
  if (error) throw error
}

/** Move a conversation into a folder. */
export async function moveToFolder(conversationId, folderId) {
  const { error } = await supabase
    .from('conversations')
    .update({ folder_id: folderId })
    .eq('id', conversationId)
  if (error) throw error
}

/** Remove a conversation from its folder. */
export async function removeFromFolder(conversationId) {
  const { error } = await supabase
    .from('conversations')
    .update({ folder_id: null })
    .eq('id', conversationId)
  if (error) throw error
}

// ── Theological term views (chat reading layer) ────────────────

/** List every term this user has dismissed — used to skip highlighting
 *  already-learned terms in MarkdownMessage.jsx. Returns lowercased term
 *  strings. */
export async function getDismissedTerms(userId) {
  const rows = await listForUser('user_term_views', { userId, select: 'term' })
  return rows.map(r => r.term)
}

/** Record that a user tapped "Got it" on a theological term — once dismissed,
 *  the chip style never re-appears for that user. Idempotent via upsert. */
export async function dismissTerm(userId, term) {
  const { error } = await supabase
    .from('user_term_views')
    .upsert({ user_id: userId, term }, { onConflict: 'user_id,term' })
  if (error) throw error
}

// ── Conversation share links ──────────────────────────────────

/** Generate a random share token from the look-alike-free alphabet. Uses
 *  crypto.getRandomValues when available (browser + modern Node) so the
 *  bytes are cryptographically random; falls back to Math.random for the
 *  rare environment without it (still ~140 bits of entropy at length 24). */
function generateShareToken() {
  const len = SHARE_TOKEN_LENGTH
  const alphabet = SHARE_TOKEN_ALPHABET
  const out = new Array(len)
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const buf = new Uint32Array(len)
    crypto.getRandomValues(buf)
    for (let i = 0; i < len; i++) out[i] = alphabet[buf[i] % alphabet.length]
  } else {
    for (let i = 0; i < len; i++) out[i] = alphabet[Math.floor(Math.random() * alphabet.length)]
  }
  return out.join('')
}

/** Create a public, revocable share link for a conversation. Returns the
 *  new row's id, the token, and the absolute URL. */
export async function createShareLink(conversationId, userId) {
  if (!conversationId || !userId) throw new Error('Missing conversationId or userId')
  try {
    const token = generateShareToken()
    const { data, error } = await supabase
      .from('shared_conversations')
      .insert({ conversation_id: conversationId, user_id: userId, share_token: token })
      .select('id, share_token')
      .single()
    if (error) throw error
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    return {
      shareId: data.id,
      token: data.share_token,
      url: `${origin}/share/${data.share_token}`,
    }
  } catch (err) {
    captureException(err, { tags: { feature: 'export', op: 'create-share' } })
    throw err
  }
}

/** Revoke a share link by id. Owner-scoped — RLS also enforces it server-side. */
export async function revokeShareLink(shareId, userId) {
  if (!shareId || !userId) throw new Error('Missing shareId or userId')
  try {
    const { error } = await supabase
      .from('shared_conversations')
      .update({ revoked_at: new Date().toISOString() })
      .eq('id', shareId)
      .eq('user_id', userId)
    if (error) throw error
  } catch (err) {
    captureException(err, { tags: { feature: 'export', op: 'revoke-share' } })
    throw err
  }
}

/** List every share row a user owns (active first, then revoked) with the
 *  joined conversation title for the management UI. */
export async function fetchUserShares(userId) {
  if (!userId) return []
  try {
    const { data, error } = await supabase
      .from('shared_conversations')
      .select('id, conversation_id, share_token, created_at, revoked_at, conversations(title)')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
    if (error) throw error
    return data || []
  } catch (err) {
    captureException(err, { tags: { feature: 'export', op: 'fetch-shares' } })
    throw err
  }
}

/** Public read of a shared conversation by token. Returns
 *  { conversation, messages } when the share is active and the underlying
 *  conversation isn't soft-deleted; null otherwise. Does NOT require auth. */
export async function fetchSharedConversation(token) {
  if (!token) return null
  try {
    const { data, error } = await supabase.rpc('get_shared_conversation', { p_token: token })
    if (error) throw error
    const row = Array.isArray(data) ? data[0] : data
    if (!row || !row.conversation_id) return null
    return {
      conversation: {
        id: row.conversation_id,
        title: row.title,
        metadata: row.metadata || {},
        created_at: row.conversation_created_at,
      },
      messages: Array.isArray(row.messages) ? row.messages : [],
    }
  } catch (err) {
    captureException(err, { tags: { feature: 'export', op: 'fetch-shared-conversation' } })
    throw err
  }
}
